// Configuration utility for Atlas Device Tracking App

import { getFirebaseProjectId } from './utils/firebaseProject';

const projectId = getFirebaseProjectId();

// When using Firebase emulators, point API to the Functions emulator (port 5002)
const useEmulators = (process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true' || process.env.REACT_APP_ENVIRONMENT === 'emulator') &&
  process.env.NODE_ENV !== 'production';
const emulatorApiUrl = useEmulators
  ? `http://127.0.0.1:5002/${projectId}/asia-south1/flaskApp`
  : null;

const config = {
  // API: when emulators enabled use Functions emulator (5002); else env override or Cloud Functions
  API_BASE_URL: (useEmulators ? emulatorApiUrl : null) || process.env.REACT_APP_API_BASE_URL || `https://asia-south1-${projectId}.cloudfunctions.net/flaskApp`,
  apiUrl: (useEmulators ? emulatorApiUrl : null) || process.env.REACT_APP_API_BASE_URL || `https://asia-south1-${projectId}.cloudfunctions.net/flaskApp`,
  
  // Environment
  ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'development',
  
  // Feature Flags
  ENABLE_DEBUG_LOGGING: process.env.REACT_APP_ENABLE_DEBUG_LOGGING === 'true',
  
  // API Endpoints
  ENDPOINTS: {
    HEALTH: '/api/health',
    DEVICES: '/api/devices/locations',
    TOWERS: '/api/towers',
    BEACHES: '/api/beaches',
    OFFICES: '/api/offices',
    OWNTRACKS: '/owntracks',
    DEVICE_DETAILS: (deviceId) => `/api/devices/${deviceId}`,
    STATS: '/api/stats'
  },
  
  // Map Configuration
  MAP: {
    DEFAULT_CENTER: [19.076, 72.8777], // Mumbai
    DEFAULT_ZOOM: 13,
    REFRESH_INTERVAL: process.env.NODE_ENV === 'production' ? 300000 : 180000, // 5 minutes in production, 3 minutes in dev
    MARKER_ICON_SIZE: [32, 32],
    // Production optimizations
    MAX_DEVICES_RENDER: process.env.NODE_ENV === 'production' ? 1000 : 500,
    DEBOUNCE_DELAY: 300
  },
  
  // UI Configuration
  UI: {
    SIDEBAR_BREAKPOINT: 768, // Mobile breakpoint
    ANIMATION_DURATION: 300,
    TOAST_DURATION: 5000
  }
};

// Helper function to build API URLs
export const buildApiUrl = (endpoint, params = {}) => {
  const url = new URL(`${config.API_BASE_URL}${endpoint}`);
  
  // Add query parameters
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.append(key, value);
    }
  });
  
  return url.toString();
};

// Helper function to get device locations URL
export const getDevicesUrl = (nameFilter = 'all') => {
  return buildApiUrl(config.ENDPOINTS.DEVICES, { name: nameFilter });
};

// Helper function to get health check URL
export const getHealthUrl = () => {
  return buildApiUrl(config.ENDPOINTS.HEALTH);
};

// Helper function to get device details URL
export const getDeviceDetailsUrl = (deviceId) => {
  return buildApiUrl(config.ENDPOINTS.DEVICE_DETAILS(deviceId));
};

// New function to get the correct device icon URL
export const getDeviceIconUrl = (deviceType, status) => {
    if (!deviceType) return '/default-marker.png';
    
    const type = deviceType.toLowerCase().trim();
    
    // Map vehicle types to icons
    switch (type) {
        case 'mobile':
            return '/mobile.png';
        case 'triton':
            return '/triton.png';
        case 'boat':
        case 'sailboat':
            return '/sailboat.png';
        case 'truck':
        case 'mini tempo':
        case 'minitempo':
        case 'hiab truck':
            return '/truck.png';
        case 'bike':
        case 'motorcycle':
        case 'scooter':
            return '/bike.png';
        case 'jeep':
        case 'car':
            return '/jeep.png';
        case 'atv':
            return '/jeep.png'; // ATV uses jeep icon
        case 'ebike':
            return '/ebike.png';
        case 'vehicle':
            return '/vehicle.png';
        case 'seahorse':
            return '/s_icon.png';
        // Add other device types as needed
        default:
            return '/default-marker.png'; // A fallback icon
    }
};

// Debug logging utility
export const debugLog = (message, data = null) => {
  if (config.ENABLE_DEBUG_LOGGING) {
    console.log(`[DEBUG] ${message}`, data);
  }
};

export default config; 